import {
  BinarySkillFilePreview,
  CsvSkillFilePreview,
  isBinarySkillFileContent,
  OpenaiYamlFormEditor,
} from "@promptlayer/ui";
import Editor from "@monaco-editor/react";
import { CodeIcon, SlidersHorizontalIcon } from "lucide-react";
import React, { useCallback, useState } from "react";

import { SkillCollectionContentHeader } from "../SkillCollectionContentHeader";
import type { SkillCollectionPreset } from "../skill-presets";
import {
  getLanguageForPath,
  isMarkdownSkillFilePath,
  isOpenaiYamlFilePath,
} from "../utils/path-utils";
import type { SkillDocumentJson } from "../utils/skill-markdown-convert";

import { CharCountBar } from "./CharCountBar";
import { RichMarkdownEditorPane } from "./RichMarkdownEditorPane";

import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/skeleton";
import { BasicTooltip } from "@/components/ui/Tooltip";

interface FileEditorProps {
  relativePath: string;
  editedContent: string | undefined;
  documentJson: SkillDocumentJson | undefined;
  isDirty: boolean;
  isDraftFile: boolean;
  hasImportedContent?: boolean;
  onClearImportFlag?: () => void;
  versionFileContent?: string;
  isLoadingVersionFiles?: boolean;
  provider?: SkillCollectionPreset | null;
  onContentChange: (content: string) => void;
  onDocumentJsonChange: (path: string, doc: SkillDocumentJson) => void;
  onEditorReady?: (doc: SkillDocumentJson) => void;
  onInitialLoad: (content: string) => void;
}

export const FileEditor = ({
  relativePath,
  editedContent,
  documentJson,
  isDirty,
  isDraftFile,
  hasImportedContent,
  onClearImportFlag,
  versionFileContent,
  isLoadingVersionFiles,
  provider,
  onContentChange,
  onDocumentJsonChange,
  onEditorReady,
  onInitialLoad,
}: FileEditorProps) => {
  const persistDocumentJson = useCallback(
    (doc: SkillDocumentJson) => onDocumentJsonChange(relativePath, doc),
    [onDocumentJsonChange, relativePath],
  );

  const serverContent = versionFileContent ?? "";
  const content = editedContent !== undefined ? editedContent : serverContent;
  const language = getLanguageForPath(relativePath);
  const useRichMarkdown = isMarkdownSkillFilePath(relativePath);
  const isOpenaiYaml = isOpenaiYamlFilePath(relativePath);
  const isBinaryPreview = isBinarySkillFileContent(content);
  const isCsvPreview = relativePath.toLowerCase().endsWith(".csv");
  const [useFormEditor, setUseFormEditor] = useState(isOpenaiYaml);

  if (
    isLoadingVersionFiles &&
    editedContent === undefined &&
    !isDraftFile &&
    versionFileContent === undefined
  ) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex h-[37px] items-center gap-2 border-b border-gray-200 bg-white px-3">
          <Skeleton className="h-4 w-48" />
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-4 bg-white p-5">
          <div>
            <Skeleton className="mb-3 h-8 w-56" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-full max-w-[640px]" />
              <Skeleton className="h-4 w-[82%]" />
              <Skeleton className="h-4 w-[68%]" />
            </div>
          </div>
          <div>
            <Skeleton className="mb-3 h-6 w-36" />
            <div className="space-y-2.5">
              <div className="flex items-center gap-3">
                <Skeleton className="h-2.5 w-2.5 rounded-full" />
                <Skeleton className="h-4 w-[48%]" />
              </div>
              <div className="flex items-center gap-3">
                <Skeleton className="h-2.5 w-2.5 rounded-full" />
                <Skeleton className="h-4 w-[56%]" />
              </div>
              <div className="flex items-center gap-3">
                <Skeleton className="h-2.5 w-2.5 rounded-full" />
                <Skeleton className="h-4 w-[40%]" />
              </div>
            </div>
          </div>
          <div>
            <Skeleton className="mb-3 h-6 w-32" />
            <div className="rounded-lg border border-gray-200/80 p-4">
              <div className="mb-3 flex gap-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-28" />
              </div>
              <div className="space-y-2">
                <div className="flex gap-3">
                  <Skeleton className="h-8 w-[30%]" />
                  <Skeleton className="h-8 w-[22%]" />
                  <Skeleton className="h-8 w-[26%]" />
                </div>
                <div className="flex gap-3">
                  <Skeleton className="h-8 w-[30%]" />
                  <Skeleton className="h-8 w-[22%]" />
                  <Skeleton className="h-8 w-[26%]" />
                </div>
                <div className="flex gap-3">
                  <Skeleton className="h-8 w-[30%]" />
                  <Skeleton className="h-8 w-[22%]" />
                  <Skeleton className="h-8 w-[26%]" />
                </div>
              </div>
            </div>
          </div>
          <div>
            <Skeleton className="mb-3 h-5 w-28" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[96%]" />
              <Skeleton className="h-4 w-[88%]" />
              <Skeleton className="h-4 w-[70%]" />
            </div>
          </div>
          <div>
            <Skeleton className="mb-3 h-6 w-40" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-full max-w-[560px]" />
              <Skeleton className="h-4 w-[84%]" />
              <Skeleton className="h-4 w-[74%]" />
            </div>
          </div>
        </div>
        <div className="border-t border-gray-200 bg-white px-3 py-2">
          <Skeleton className="h-3 w-32" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <SkillCollectionContentHeader
        filePath={relativePath}
        trailingContent={
          <>
            {isDirty ? (
              <Badge variant={BadgeVariant.AMBER} size={BadgeSize.SM} text="Modified" />
            ) : null}
            {isOpenaiYaml ? (
              <BasicTooltip
                content={useFormEditor ? "Switch to raw editor" : "Switch to form editor"}
              >
                <button
                  type="button"
                  className="flex items-center justify-center rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  onClick={() => setUseFormEditor(prev => !prev)}
                >
                  {useFormEditor ? (
                    <CodeIcon className="h-4 w-4" />
                  ) : (
                    <SlidersHorizontalIcon className="h-4 w-4" />
                  )}
                </button>
              </BasicTooltip>
            ) : null}
          </>
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto bg-white">
        {isBinaryPreview && <BinarySkillFilePreview content={content} />}
        {!isBinaryPreview && isCsvPreview && (
          <CsvSkillFilePreview content={content} editable onContentChange={onContentChange} />
        )}
        {!isBinaryPreview && !isCsvPreview && useRichMarkdown && (
          <RichMarkdownEditorPane
            key={relativePath}
            editedContent={editedContent}
            isDraftFile={isDraftFile}
            dataContent={versionFileContent}
            documentJson={documentJson}
            fallbackMarkdown={content}
            hasImportedContent={hasImportedContent}
            onClearImportFlag={onClearImportFlag}
            onInitialLoad={onInitialLoad}
            onDocumentJsonChange={persistDocumentJson}
            onEditorReady={onEditorReady}
          />
        )}
        {!isBinaryPreview && !isCsvPreview && !useRichMarkdown && isOpenaiYaml && useFormEditor && (
          <OpenaiYamlFormEditor content={content} onChange={onContentChange} />
        )}
        {!isBinaryPreview &&
          !isCsvPreview &&
          !useRichMarkdown &&
          !(isOpenaiYaml && useFormEditor) && (
            <Editor
              height="100%"
              defaultLanguage={language}
              language={language}
              value={content}
              onChange={value => onContentChange(value ?? "")}
              options={{
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 13,
                lineHeight: 20,
                padding: { top: 12, bottom: 12 },
                automaticLayout: true,
              }}
            />
          )}
      </div>
      {!isBinaryPreview && !isCsvPreview && (
        <CharCountBar provider={provider} relativePath={relativePath} charCount={content.length} />
      )}
    </div>
  );
};
